// TimeShift.tsx — Pattern 2 (TIMELINE SCRUBBING) backbone for the v4 shell.
//
// `useTimeShift()` exposes `asOf` (null = live). The active screen is re-rendered
// keyed by `asOf` so scrubbing to a past moment re-queries "as of" that time. The
// TimeScrubber UI + asOf-parameterised queries land in a follow-up slice; this
// provider is the wiring backbone the shell keys off today (asOf stays null/live).

import { createContext, useContext, useMemo, useState } from "react";

export interface TimeShiftValue {
  /** ISO timestamp to view state "as of", or null for live. */
  asOf: string | null;
  setAsOf: (asOf: string | null) => void;
  /** true when viewing live (not scrubbed). */
  isLive: boolean;
}

const TimeShiftContext = createContext<TimeShiftValue | null>(null);

export function TimeShiftProvider({ children }: { children: React.ReactNode }) {
  const [asOf, setAsOf] = useState<string | null>(null);
  const value = useMemo<TimeShiftValue>(
    () => ({ asOf, setAsOf, isLive: asOf === null }),
    [asOf],
  );
  return (
    <TimeShiftContext.Provider value={value}>
      {children}
    </TimeShiftContext.Provider>
  );
}

export function useTimeShift(): TimeShiftValue {
  const ctx = useContext(TimeShiftContext);
  if (!ctx) {
    // Defensive default so screens can be rendered outside the provider in tests.
    return { asOf: null, setAsOf: () => {}, isLive: true };
  }
  return ctx;
}
